import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { MdShare, MdInfo } from "react-icons/md";
import { useGroupedCost } from "../../../hooks/useGroupedCost";

const COLORS = [
  "#dc2626", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6",
  "#ec4899", "#06b6d4", "#84cc16", "#ef4444", "#6366f1",
  "#14b8a6", "#d946ef", "#f97316", "#22c55e", "#eab308",
];

const SharedCostsAndRules = ({ selectedYear, selectedMonth }) => {
  const { getSharedCostsByService } = useGroupedCost();
  const [sharedCostsData, setSharedCostsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedYear && selectedMonth) {
      fetchSharedCosts();
    }
  }, [selectedYear, selectedMonth]);

  const fetchSharedCosts = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getSharedCostsByService(selectedYear, selectedMonth);
      if (result.success && result.data) {
        const formattedData = result.data.sharedCostsByService.map(item => ({
          name: item.serviceName,
          cost: item.totalCost
        }));
        setSharedCostsData(formattedData);
      } else {
        setError(result.message || "Failed to load shared costs");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount || 0);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-lg">
          <p className="text-gray-300 text-sm mb-1">{label}</p>
          <p className="text-primary-cyan text-lg font-bold">
            {formatAmount(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-primary-dark rounded-2xl p-6 border border-gray-700/50">
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-3">Loading shared costs...</p>
          </div>
        </div>
        <div className="bg-primary-dark rounded-2xl p-6 border border-gray-700/50">
          <div className="text-center py-8">
            <div className="inline-block w-8 h-8 border-4 border-primary-cyan border-t-transparent rounded-full animate-spin"></div>
            <p className="text-gray-400 mt-3">Loading rules...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 mx-3 lg:grid-cols-2 gap-6">
      <div className="bg-primary-dark rounded-2xl p-6 border border-gray-700/50">
        <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
          <MdShare className="text-primary-cyan text-2xl" />
          <h3 className="text-xl font-semibold text-white">Shared Costs by Service</h3>
        </div>

        {error ? (
          <div className="text-center py-8">
            <p className="text-red-400">{error}</p>
          </div>
        ) : sharedCostsData.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-400">No shared cost data available</p>
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={450}>
              <BarChart
                data={sharedCostsData}
                margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                layout="vertical"
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
                <XAxis
                  type="number"
                  stroke="#9CA3AF"
                  tick={{ fill: "#9CA3AF", fontSize: 12 }}
                  tickFormatter={(value) => `$${value}`}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="#9CA3AF"
                  tick={{ fill: "#9CA3AF", fontSize: 11 }}
                  width={120}
                />
                <Tooltip content={<CustomTooltip />} cursor={false} />
                <Bar
                  dataKey="cost"
                  name="Cost"
                  radius={[0, 8, 8, 0]}
                  animationDuration={1000}
                  activeBar={false}
                >
                  {sharedCostsData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4 flex flex-wrap justify-center gap-3">
              {sharedCostsData.map((item, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                  />
                  <span className="text-gray-300 text-xs truncate max-w-[120px]">
                    {item.name.length > 20 ? item.name.substring(0, 17) + "..." : item.name}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="bg-primary-dark rounded-2xl p-6 border border-gray-700/50">
        <div className="flex items-center gap-2 mb-6 pb-3 border-b border-gray-700">
          <MdInfo className="text-primary-cyan text-2xl" />
          <h3 className="text-xl font-semibold text-white">Calculation Rules</h3>
        </div>

        <div className="space-y-4">
          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h4 className="text-primary-cyan font-medium mb-2">RESERVATION VM</h4>
            <p className="text-gray-300 text-sm">
              Total Cost = <span className="text-white">Direct Cost</span> +{" "}
              <span className="text-white">Reservation Cost / </span> 
              <span className="text-white">Shared Cost</span>
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h4 className="text-primary-cyan font-medium mb-2">PAYG VM</h4>
            <p className="text-gray-300 text-sm">
              Total Cost = <span className="text-white">Direct Cost / </span> 
              <span className="text-white">Shared Cost</span>
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h4 className="text-primary-cyan font-medium mb-2">Shared Cost Calculation</h4>
            <p className="text-gray-300 text-sm">
              Shared Cost per VM = <span className="text-white">Total Shared Costs / </span> 
              <span className="text-white">Total Number of VMs</span>
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h4 className="text-primary-cyan font-medium mb-2">Reservation Cost Calculation</h4>
            <p className="text-gray-300 text-sm">
              Reservation Cost per VM = <span className="text-white">Reservation Cost / </span> 
              <span className="text-white">Number of VMs with same VM Type (D2ls v5 ,D2s v5 ...)</span>
            </p>
          </div>

          <div className="bg-gray-800/50 rounded-lg p-4 border border-gray-700">
            <h4 className="text-primary-cyan font-medium mb-2">Direct Cost</h4>
            <p className="text-gray-300 text-sm">
              Direct Cost = <span className="text-white">Costs directly linked to the specific VM</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedCostsAndRules;